<template>
  <div :class="dev.isBar ? 'devMode' : ''">
    <div :class="dev.isBar ? {'barAlign': true, [dev.barDevColor]: true} : ''">
      {{ dev.barText }}
    </div>
    <v-app-bar
      app
      :clippedLeft="false"
      dark
      :class="dev.isBar ? 'navbar' : ''"
      color="#2A3E53"
      :flat="true"
    >
      <v-row
        class="tabs--wrapper"
      >
        <v-col
          cols="auto"
          class="p-0"
          alignSelf="center"
        >
          <v-app-bar-nav-icon
            class="hidden-lg-and-up"
            style="color: #2A3E53"
            @click="toggleDrawer(true)"
          />
        </v-col>
        <v-col
          cols="8"
          class="hidden-md-and-down p-0"
        >
          <v-tabs
            backgroundColor="white"
            sliderColor="#EF4135"
            :dark="false"
            class="pages-tabs"
            hideSlider
          >
            <v-tab
              v-for="pageName in mainNavItems"
              :id="`nav-item-${pageName}`"
              :key="pageName + activePathName"
              :to="`/${pageName}`"
              :data-cy="`${dataCy.navBar.tabs.page}-${pageName}`"
              :style="isActiveItem(pageName) ? 'color:#2A3E53; border-bottom: 2px solid red' : 'color: #2A3E53'"
              class="pageName"
              :activeClass="isActiveItem(pageName) ? 'pageName-active': null"
            >
              <span :style="isActiveItem(pageName) ? 'font-weight: bold' : 'font-weight: 300'">{{ $t(`${pageName}.pageName`) }}</span>
            </v-tab>
          </v-tabs>
        </v-col>
        <v-col
          md="2"
          cols="auto"
          class="p-0"
          style="margin-left: auto !important; max-height: 100% !important"
        >
          <v-toolbar-items class="navbar-toolbar-items">
            <v-menu
              bottom
              offsetY
            >
              <template v-slot:activator="{on}">
                <v-tab
                  v-on="on"
                  @click="gotoAlertsNotifications()"
                >
                  <v-badge
                    :color="unacknowledgedAlerts ? 'red' : ''"
                    :content="unacknowledgedAlerts ? unacknowledgedAlerts : ''"
                    overlap
                  >
                    <v-icon
                      color="#2A3E53"
                      right
                    >
                      notifications_none
                    </v-icon>
                  </v-badge>
                </v-tab>
              </template>
            </v-menu>
            <SwitchLang />
            <v-menu
              v-if="accountDropdownItems.length || isLoggedIn"
              bottom
              offsetY
            >
              <template v-slot:activator="{on}">
                <v-tab
                  id="profile-dropdown-menu"
                  v-on="on"
                >
                  <LetterAvatar
                    :label="teamName"
                    :extraClass="'darken'"
                  />
                </v-tab>
              </template>
              <v-list>
                <v-list-item>
                  <strong>
                    {{ email | shorten(50) }}
                  </strong>
                </v-list-item>
                <div
                  v-for="pageName in accountDropdownItems"
                  :key="pageName"
                >
                  <v-list-item
                    v-if="shouldShowLink(`/${pageName}`)"
                    :to="`/${pageName}`"
                    @click="onMenuPathChange(`/${pageName}`)"
                  >
                    {{ $t(`${pageName}.title`) }}
                    <strong
                      v-if="pageName === 'teams'"
                      class="ml-1"
                    >
                      ({{ teamName }})
                    </strong>
                  </v-list-item>
                </div>
                <v-list-item
                  :to="`/${'alertsNotifications'}`"
                >
                  {{ $t('alertsNotifications.title') }}
                </v-list-item>
                <v-list-item
                  :to="`/${'appointment'}`"
                >
                  {{ $t('appointment.title') }}
                </v-list-item>
                <v-list-item
                  :to="`/${'training'}`"
                >
                  {{ $t('training.title') }}
                </v-list-item>
                <v-list-item
                  v-if="isLoggedIn"
                  @click="onClickSendFeedback"
                >
                  {{ $t('feedback.title') }}
                </v-list-item>
                <v-list-item
                  v-if="isLoggedIn"
                  @click="onClickReleaseNotes"
                >
                  {{ $t('administration.release') }}
                </v-list-item>
                <v-list-item
                  v-if="isLoggedIn"
                  @click="onClickLogout"
                >
                  {{ $t("logout.pageName") }}
                </v-list-item>
              </v-list>
            </v-menu>
            <v-divider
              class="mx-3 aq-community-divider"
              vertical
            />
            <v-menu
              bottom
              offsetY
            >
              <template v-slot:activator="{on}">
                <v-btn
                  class="aq-community-btn-circle"
                  text
                  href="https://community.aquassist.com/"
                  target="_blank"
                  rel="noopener"
                  style="color: #2A3E53; font-weight: 500;"
                  v-on="on"
                >
                  <v-icon
                    left
                    style="margin-right: 4px;"
                  >
                    mdi-web-sync
                  </v-icon>
                  {{ $t('forms.labels.aqCommunity') }}
                </v-btn>
              </template>
            </v-menu>
          </v-toolbar-items>
        </v-col>
      </v-row>
    </v-app-bar>
  </div>
</template>

<script>
import Vue from 'vue';
import SendFeedbackForm from '@/components/SendFeedbackForm';
import accessControlMixin from '@/mixins/AccessControlMixin';
import { mapGetters, mapActions } from 'vuex';
import navItems from '@/constants/navItems';
import SwitchLang from '@/components/shared/SwitchLang';
import { navBar } from '@/aq-data-cy.json';
import LetterAvatar from '../LetterAvatar.vue';

export default {
    components: { LetterAvatar, SwitchLang },
    mixins: [
        accessControlMixin,
    ],
    data() {
        return {
            activePathName: undefined,
            langs: ['en', 'fr'],
            dev: {
                isBar: false,
                barDevColor: '',
                barText: '',
            },
        };
    },

    computed: {
        ...mapGetters([
            'isLoggedIn',
            'teamIndex',
            'teamsCount',
            'lang',
            'email',
            'teamName',
            'roles',
            'unacknowledgedAlerts',
            'devMode',
        ]),


        switchLangTo() {
            const cLang = this.$i18n.locale; // set the language
            return this.langs.filter(lang => lang !== cLang); // display all languages except the one selected
        },
        mainNavItems() {
            return (!this.isLoggedIn || this.teamIndex === -1)
                ? []
                : navItems.filter(item => item.roles.some(role => this.hasRole(role))).map(item => item.name);
        },
        accountDropdownItems() {
            let arr = [];
            if (!this.isLoggedIn) return arr;
            // Can access profile if isLoggedIn
            arr = ['profile'];
            // Can access dashboard if has a selected team
            if (this.teamIndex !== -1) {
                arr.push('team');
            }
            // Can switch team if more than 1 team
            if (this.teamsCount > 1) {
                arr.push('teams');
            }
            return arr;
        },
        dataCy() {
            return {
                navBar,
            };
        },
    },
    watch: {
        $route(to) {
            const currentPath = to.path;
            this.activePathName = currentPath;
        },
    },
    created() {
        this.$i18n.locale = this.lang;
        // just for the unit tests, to check that i18n.locale is well set after changling language
        this.$store.watch(
            (state, getters) => getters.lang,
            () => {
                this.$i18n.locale = this.lang;
            },
        );
        this.disableDevMode();
        this.checkIsDevMode();

        // eslint-disable-next-line prefer-destructuring
        this.activePathName = this.$route.path.split('/')[1];
    },
    methods: {
        ...mapActions({
            setLang: 'setLang',
            logout: 'logout',
            toggleDrawer: 'toggleDrawer',
            turnOnTutorial: 'turnOnTutorial',
            activeDevMode: 'activeDevMode',
            disableDevMode: 'disableDevMode',
        }),

        isActiveItem(pageName) {
            // Cas spécifique ou l'on est dans client site ==> On veut que site soit active
            if (pageName === 'clients') {
                if (!this.$route.path.includes('sites') && this.$route.path.includes('clients')) return true;
                return false;
            }
            if (pageName === 'sites') {
                if (this.$route.path.includes('sites')) return true;
                return false;
            }
            // Autres cas
            if (pageName === this.activePathName.split('/')[1]) return true;

            return false;
        },


        onClickSendFeedback() {
            this.$root.$emit('showModal', {
                title: this.$t('feedback.title'),
                component: Vue.component('SendFeedbackForm', SendFeedbackForm),
                buttonTitle: 'send',
                onConfirm: requestPayload => this.sendFeedback(requestPayload),
            });
        },
        async sendFeedback(requestPayload) {
            const payload = this.$utils.removeEmptyNullOrUndefined(requestPayload, true);

            await this.$rq.sendFeedback(payload);
            this.$toasted.global.toast_success({
                message: this.$t('msg.feedbackSent'),
            });
        },
        onClickChangeLang(lang) {
            this.setLang(lang);
        },
        onClickLogout() {
            this.logout();
        },
        onClickTourGuide() {
            this.turnOnTutorial('overview');
            this.$tourGuide.start();
        },
        gotoAlertsNotifications() {
            this.$router.push({
                name: 'alertsNotifications',
            });
        },
        checkIsDevMode() {
            const { VUE_APP_ENV_COLOR, VUE_APP_ENV_LABEL } = process.env;

            /* On modifie la barre en haut si nous ne sommes pas en production */
            if (VUE_APP_ENV_COLOR && VUE_APP_ENV_LABEL && VUE_APP_ENV_LABEL !== 'PRODUCTION') {
                this.dev.isBar = true;
                this.dev.barDevColor = VUE_APP_ENV_COLOR;
                this.dev.barText = VUE_APP_ENV_LABEL;
                this.activeDevMode();
            }
        },
        onClickReleaseNotes() {
            this.$emit('openReleaseNotes');
        },
    },
};
</script>

<style lang="scss" scoped>
  .tabs--wrapper {
    background-color: white;
    height: 100%;
    border-top-right-radius: 20px;
    border-top-left-radius: 20px;
    margin: 0px;
    overflow: hidden;
    border-bottom: 0.5px solid #26384B;
    padding-left: 20px;
    padding-right: 20px;
  }
  .pages-tabs {
    height: 100% !important;
  }
  ::v-deep .theme--dark.v-tabs > .v-tabs-bar {
    height: 100%;
  }
  ::v-deep .v-toolbar__content {
      padding: 6px;
      padding-bottom: 0px;
   }
  a:not(.v-list-item) {
    color: white;
  }

  a, a:hover {
    text-decoration: none;
  }
  .navbar-toolbar-items {
    flex: 1;
    padding:5px;
    justify-content: flex-end;
    .v-tab {
      padding: 5px;
      min-width: 52px;
    }
    background-color: white !important;
    height: 100%;
  }
  .pageName {
    letter-spacing: normal;
    text-transform: capitalize !important;
    font-style: normal;
    font-weight: 400;
    font-size: 15px;
    line-height: 18px;
  }
  .pageName-active {
    font-weight: 700 !important;
  }
.devMode {
  margin-bottom: 20px
}

.navbar {
  top: 20px;
}

// If we are in devMode
.barAlign{
  text-align: center;
  position: fixed;
  width: 100%;
  z-index: 10000000;
  text-transform: capitalize;
  color: white;
  font-weight: 500
}

  .aq-community-divider {
    height: 32px !important;
    align-self: center;
    background: #d3dbe3 !important;
    opacity: 1 !important;
  }
  .aq-community-separator {
    display: inline-block;
    width: 1px;
    height: 32px;
    background: #d3dbe3;
    margin: 0 16px 0 0;
    vertical-align: middle;
  }

.aq-community-btn-circle {
    border: 2px solid #2A3E53;
    border-radius: 50px; /* Large value for pill/circle */
    background: #fff;
    padding: 4px 16px;
    min-width: 44px;
    min-height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 2px 6px rgba(42, 62, 83, 0.07);
    transition: box-shadow 0.2s;
    text-transform : none;
  }
</style>
